/* The rank widget's data, whichever source filled it.

   Two things can drive the bar at the top of the sidebar and they are not the
   same quantity. Smartico measures **XP** — a points balance a player earns and
   can spend. The platform measures **tier progress** — wagering, and on some
   tiers deposits, which only ever goes up. The widget looks identical either
   way, so the shape is shared and the label is not: only the Smartico source
   writes "XP", because only there is the number XP.

   docs/findings/xp-is-smartico-points-not-tier-progress.md */

export type LevelView = {
  name: string;
  nextName: string | null;
  imageUrl: string | null;
  nextImageUrl: string | null;
  /* 0–100. */
  percent: number;
  /* What sits in the middle of the bar. */
  label: string;
  /* The bar's numbers in full, for the hover — compact notation is for the
     bar, where "1.2K/2.5K" fits and "1,234 / 2,500" does not. */
  exact: string;
  /* What is left to the next level, as a phrase, or null at the top of the
     ladder or once the target is met. The whitelabel's hover has the same two
     lines. */
  toNext: string | null;
  /* The same fact as a sentence, for a screen reader. */
  spoken: string;
  /* Progression cannot change this tier (a hard lock, or an externally
     managed tier): the crest carries a lock. Only the platform's tier source
     says so; Smartico levels never lock. */
  locked?: boolean;
};

const FULL = new Intl.NumberFormat("en", { maximumFractionDigits: 2 });

export const fullNumber = (value: number) => FULL.format(value);

export const fullPair = (current: number, target: number) =>
  `${fullNumber(current)} / ${fullNumber(target)}`;

/* Compact notation, widened only as far as it takes to tell the two numbers
   apart. At one fraction digit 49.6 and 50 both render "50", and a bar reading
   "50/50" while the player is short is the kind of wrong that looks fine. */
const COMPACT = [1, 2, 3].map(
  (maximumFractionDigits) =>
    new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits,
      roundingMode: "trunc",
    }),
);

export const compactPair = (current: number, target: number) => {
  let pair = "";

  for (const format of COMPACT) {
    const left = format.format(current);
    const right = format.format(target);

    pair = `${left}/${right}`;

    /* Once they read differently there is nothing left to disambiguate — and a
       player who really has reached the target should see "50/50". */
    if (left !== right || current >= target) {
      return pair;
    }
  }

  return pair;
};

export const levelPercent = (current: number, target: number) => {
  if (!Number.isFinite(current) || !Number.isFinite(target) || target <= 0) {
    return current > 0 ? 100 : 0;
  }

  return Math.min(100, Math.max(0, (current / target) * 100));
};
